import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FiCode, FiSmartphone, FiCpu, FiSearch, FiPenTool, FiTrendingUp, FiArrowRight } from "react-icons/fi";
import HeroBanner from "../components/HeroBanner";
import SectionWrapper from "../components/SectionWrapper";
import servicesHero from "../assets/services-hero.jpg";

const serviceCategories = [
  {
    icon: FiCode,
    title: "Software Solutions",
    desc: "Custom software development, ERP, CRM, SaaS platforms, and enterprise dashboards built with cutting-edge technologies.",
    path: "/software-solutions",
  },
  {
    icon: FiSmartphone,
    title: "Web & App Development",
    desc: "Responsive business websites, progressive web apps, Android & iOS applications, and admin panels.",
    path: "/services/web-app-development",
  },
  {
    icon: FiCpu,
    title: "AI, Data & Automation",
    desc: "Business intelligence dashboards, AI-based recommendation systems, process automation, and predictive analytics.",
    path: "/services/ai-data-automation",
  },
  {
    icon: FiSearch,
    title: "SEO & Performance Marketing",
    desc: "Technical SEO, Google Ads, Meta Ads, conversion rate optimization, and funnel design.",
    path: "/services/seo-performance-marketing",
  },
  {
    icon: FiPenTool,
    title: "Branding & Creative Solutions",
    desc: "Brand identity, UI/UX design, motion graphics, and creative assets that convert attention into action.",
    path: "/services/branding-creative",
  },
  {
    icon: FiTrendingUp,
    title: "Digital Marketing Solutions",
    desc: "Data-driven social media marketing, content strategy, community management, and analytics.",
    path: "/digital-marketing",
  },
];

const engagementModels = [
  { title: "Fixed Cost Projects", desc: "Clear scope, defined timeline, and predictable costs." },
  { title: "Dedicated Development Teams", desc: "Your extended team working exclusively on your project." },
  { title: "Monthly Digital Marketing Retainers", desc: "Ongoing optimization and growth strategies." },
  { title: "Enterprise Custom Solutions", desc: "Tailored solutions for large-scale requirements." },
];

const Services = () => {
  return (
    <>
      <HeroBanner
        image={servicesHero}
        title="Our Services"
        subtitle="End-to-end digital services that align technology, data, and marketing for business growth."
      />

      <SectionWrapper>
        <div className="text-center mb-12">
          <h2 className="section-title mb-4">Our <span className="text-primary">Capabilities</span></h2>
          <p className="section-subtitle mx-auto">
            We provide end-to-end digital services that align technology, data, and marketing for business growth.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {serviceCategories.map((svc, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
            >
              <Link to={svc.path} className="card-hover block group h-full">
                <svc.icon className="w-12 h-12 text-primary mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="font-montserrat font-bold text-xl mb-3">{svc.title}</h3>
                <p className="text-muted-foreground font-roboto text-sm mb-4 leading-relaxed">{svc.desc}</p>
                <span className="text-primary font-semibold text-sm flex items-center gap-2 group-hover:gap-3 transition-all">
                  Learn More <FiArrowRight />
                </span>
              </Link>
            </motion.div>
          ))}
        </div>
      </SectionWrapper>

      <SectionWrapper className="bg-muted">
        <div className="text-center mb-12">
          <h2 className="section-title mb-4">Engagement <span className="text-primary">Models</span></h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {engagementModels.map((model, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="card-hover text-center"
            >
              <div className="w-12 h-12 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-montserrat font-bold text-lg mx-auto mb-4">
                {i + 1}
              </div>
              <h3 className="font-montserrat font-bold text-lg mb-2">{model.title}</h3>
              <p className="text-muted-foreground font-roboto text-sm">{model.desc}</p>
            </motion.div>
          ))}
        </div>
      </SectionWrapper>

      <SectionWrapper className="bg-primary text-primary-foreground text-center">
        <h2 className="text-3xl md:text-4xl font-montserrat font-bold mb-4">Let's Discuss Your Project</h2>
        <p className="text-primary-foreground/80 font-roboto text-lg mb-8 max-w-2xl mx-auto">
          From idea to execution, we're here to help you succeed.
        </p>
        <Link to="/contact" className="btn-hero-outline">Get Started</Link>
      </SectionWrapper>
    </>
  );
};

export default Services;
